/**
 * MonetizationProvider — the ONE place the popup lives, and the ONE way to open it.
 *
 * Two pieces, deliberately separate (the glowtest_FE split, PO 2026-09-20):
 *   - WHAT it shows  = MonetizationDialog + plans.ts   (the stone ladder)
 *   - WHEN it shows  = this provider + useMonetizationTrigger (below)
 *
 * Why centralised: a paywall that can be rendered from three places becomes three different
 * paywalls. Any surface that wants the user to pay, upgrade or add credits calls
 * `useMonetization().open(reason)` and nothing else. The dialog is mounted once, in the CRM shell.
 *
 * Honest-zero note: `credits` starts as `undefined`, NOT 0. "We have not read the balance yet" and
 * "the balance is zero" are different facts and must never be rendered as the same thing.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { MonetizationDialog, type MonetizationReason } from "./MonetizationDialog";
import { useMonetizationTrigger } from "./useMonetizationTrigger";

interface MonetizationContextValue {
  open: (reason?: MonetizationReason) => void;
  close: () => void;
  /** Credits in the wallet, or `undefined` when it has not been read. Never faked to 0. */
  credits: number | undefined;
  setCredits: (n: number | undefined) => void;
  /**
   * The plan this payer is on (`starter`/`growth`/`scale`), or `undefined` when it has not been read or
   * he is on none. Read from the same balance response as `credits`, so it costs no extra call.
   *
   * ⚠️ `undefined` here means "unknown", NOT "free". Every surface that prices an action must show
   * nothing on unknown: the price it states must be the price the server will DEBIT, and only the
   * server knows the payer's rate (196 on a free account — priced at the entry tier — then 196 / 172 /
   * 164 up the ladder).
   */
  tier: string | undefined;
  /**
   * What ONE reachout costs THIS payer, in credits — the figure the backend will actually debit,
   * read straight from the same response (`per_send_credit_cost`) rather than re-derived from the
   * ladder. `undefined` means not read yet, and a surface that prices an action must then say
   * nothing rather than guess: the rate depends on the payer's tier (196 free, then 196 / 172 / 164).
   *
   * WHY IT EXISTS. The rate used to be looked up from `PLANS` by tier, which is EMPTY for a free
   * payer — so the one person who most needs to see what a batch costs (he has exactly five free
   * sends) saw no price at all. The server states the rate for every payer, so the preview no longer
   * has to infer it, and cannot be wrong by 10× when it does.
   */
  perSendCost: number | undefined;
  /** Remember that we already showed the wall once this session, so it never nags. */
  shownThisSession: boolean;
}

const MonetizationContext = createContext<MonetizationContextValue | null>(null);

export function useMonetization(): MonetizationContextValue {
  const ctx = useContext(MonetizationContext);
  if (!ctx) throw new Error("useMonetization must be used inside <MonetizationProvider>");
  return ctx;
}

export function MonetizationProvider({ children }: { children: ReactNode }) {
  const [open, setOpenState] = useState(false);
  const [reason, setReason] = useState<MonetizationReason>("plan");
  const [credits, setCredits] = useState<number | undefined>(undefined);
  const [tier, setTier] = useState<string | undefined>(undefined);
  const [perSendCost, setPerSendCost] = useState<number | undefined>(undefined);
  const [shownThisSession, setShownThisSession] = useState(false);

  const openWith = useCallback((r: MonetizationReason = "plan") => {
    setReason(r);
    setOpenState(true);
    setShownThisSession(true);
  }, []);

  const close = useCallback(() => setOpenState(false), []);

  const value = useMemo<MonetizationContextValue>(
    () => ({ open: openWith, close, credits, setCredits, tier, perSendCost, shownThisSession }),
    [openWith, close, credits, tier, perSendCost, shownThisSession],
  );

  useMonetizationTrigger({
    credits,
    shownThisSession,
    open: openWith,
    setCredits,
    setTier,
    setPerSendCost,
  });

  return (
    <MonetizationContext.Provider value={value}>
      {children}
      <MonetizationDialog
        open={open}
        reason={reason}
        onClose={close}
        credits={credits ?? null}
        currentTier={tier ?? null}
      />
    </MonetizationContext.Provider>
  );
}
